import React from 'react'
import {
    IonPage,
    IonContent,
	IonButton,
	useIonAlert,
	AlertInput,
	AlertButton,
} from '@ionic/react'
import List from './List'
import Header from './Header'
import { SEASON_RULE_MALE_MIXED } from './definitions'
import {
	useCanView,
    useMyPlayers,
	useMySeasonRules,
	useSection,
	useTeamSeasons,
} from './data'
import FillinIcon from './FillinIcon'
import MaleIcon from './MaleIcon'
import {useImportPreviousSeasonPlayers} from './functions'

const Players = () => {

    const data = useMyPlayers()
	const canView = useCanView()
	const seasonRules = useMySeasonRules()
	const showMaleField = seasonRules === SEASON_RULE_MALE_MIXED

	const type: ItemTypeDefinition<Player> = {
		name: 'players',
		unit: 'player',
		fields: [
			'name',
			...(showMaleField ? [ 'isMale' as const ] : [ ]),
			'isFillin',
		],
		optionalFields: [ 'isMale', 'isFillin' ],
		linkToItem: true,
		requireValidSeason: true,
		quickAdding: true,
		sortFunction: (a, b) => (a.isFillin ? 1 : 0) - (b.isFillin ? 1 : 0) || (a.name ? a.name.localeCompare(b.name) : -1),
        isInvalid: {
            name: ({ value, items, item }) => {
				console.log("Checking name uniqueness", value, items, item)
                if (items?.filter(obj => obj.name === value && obj.id !== item.id).length) {
                    return 'Name must be unique'
                }
                return false
            },
        },
		formatField: props => {
			if (props.field === 'isMale') {
				return <IsMale value={!!props.value} />
			}
			if (props.field === 'isFillin') {
				return <IsFillin value={!!props.value} />
			}
			return <>{String(props.value)}</>
		},
	}

	return (
		<IonPage>
            <IonContent>
				{
					canView ? (
						<List<Player>
							data={data}
							type={type}
							/>
					) : (
						<div className="private-season">
							🔒 This season is private.
						</div>
					)
				}
			</IonContent>
            <Header allowTeamChange={true} />
        </IonPage>
	)
}

type IsFillinProps = {
	value: boolean | undefined
}

export const IsFillin = (props: IsFillinProps) => {
	if (!props.value)
		return null
	return (
		<div className="isFillin">
			<FillinIcon />
			<span className="fillin-label">Fill-in</span>
		</div>
	)
}

type IsMaleProps = {
	value: boolean | undefined
}

export const IsMale = (props: IsMaleProps) => {
	const seasonRules = useMySeasonRules()
	if (seasonRules !== SEASON_RULE_MALE_MIXED || !props.value)
		return null
	return (
		<div className="isMale">
			<MaleIcon />
			<span className="male-label">Male</span>
		</div>
	)
}

export const OfferPlayerImport = () => {

	const [ presentAlert ] = useIonAlert()
	const { team, season } = useSection()
	const importPreviousSeasonPlayers = useImportPreviousSeasonPlayers()

	const allSeasons = useTeamSeasons(team || '')

	if (!allSeasons)
		return null

	const otherSeasons = Object.keys(allSeasons).filter(seasonId => seasonId !== season)

	console.log('other seasons', otherSeasons)

	if (!otherSeasons.length) {
		return null
	}

	const onClick = () => {
		const sortedSeasons = otherSeasons.sort((a, b) => allSeasons[b].created - allSeasons[a].created)
		const okButton: AlertButton = {
			text: 'OK',
			cssClass: 'import-season-confirm',
			htmlAttributes: {
				disabled: sortedSeasons.length !== 1,
			},
			handler: importPreviousSeasonPlayers,
		}
		const inputs = sortedSeasons.map<AlertInput>(seasonId => ({
			type: 'radio',
			label: allSeasons[seasonId].name,
			value: seasonId,
			checked: sortedSeasons.length === 1,
			handler: () => {
				if (okButton.htmlAttributes) {
					okButton.htmlAttributes.disabled = false
				}
			},
		}))
		presentAlert({
			header: "Select season",
			subHeader: "Players will be imported from this season.",
			inputs,
			buttons: [
				'Cancel',
				okButton,
			],
		})
	}

	return (
		<IonButton
			onClick={onClick}
		>
			Import from previous season
		</IonButton>
	)
}

export default Players
